import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { CircleUser, Moon, Sun, Monitor, GraduationCap, Settings2 } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useMode } from '../../hooks/useMode';
import { SocketStatusIndicator } from './SocketStatusIndicator';
import { CooldownHeaderIndicator } from './CooldownHeaderIndicator';
import { cn } from '../../lib/utils';

interface HeaderProps {
  email: string | null;
  onLogout: () => void;
  menuButton?: React.ReactNode;
}

export function Header({ email, onLogout, menuButton }: HeaderProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { mode, setMode, isLoading } = useMode();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center justify-between gap-2 px-3 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {menuButton}
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="truncate text-base font-bold sm:text-lg">Breakout Engine Admin</span>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {!isLoading && (
            <div
              className="hidden rounded-md border p-0.5 sm:flex"
              role="group"
              aria-label="Interface mode"
            >
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 gap-1.5 px-2.5 text-xs',
                  mode === 'beginner' && 'bg-muted'
                )}
                onClick={() => setMode('beginner')}
                aria-pressed={mode === 'beginner'}
                aria-label="Beginner mode"
              >
                <GraduationCap className="h-3.5 w-3.5" />
                Beginner
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 gap-1.5 px-2.5 text-xs',
                  mode === 'advanced' && 'bg-muted'
                )}
                onClick={() => setMode('advanced')}
                aria-pressed={mode === 'advanced'}
                aria-label="Advanced mode"
              >
                <Settings2 className="h-3.5 w-3.5" />
                Advanced
              </Button>
            </div>
          )}

          <CooldownHeaderIndicator />

          <SocketStatusIndicator />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                {resolvedTheme === 'dark' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
                <span className="sr-only">Toggle theme</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun className="mr-2 h-4 w-4" />
                Light
                {theme === 'light' && <span className="ml-auto">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon className="mr-2 h-4 w-4" />
                Dark
                {theme === 'dark' && <span className="ml-auto">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>
                <Monitor className="mr-2 h-4 w-4" />
                System
                {theme === 'system' && <span className="ml-auto">✓</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {email ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <CircleUser className="h-5 w-5" />
                  <span className="hidden sm:inline">{email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground">{email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm">
              <Link to="/login">Login</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
